/**
 * ViewManager - Manages view lifecycle and transitions
 *
 * Responsibilities:
 * - Track current active view
 * - Handle view transitions (exit old, enter new)
 * - Manage available views
 * - Apply view modes (2D/3D camera settings)
 *
 * Benefits:
 * - Centralized view state management
 * - Guarantees proper onExit/onEnter lifecycle
 * - Simplifies persona/mode/tool view switching
 */

import { Viewer } from '../viewer/viewer';
import { IViewMode } from './types';
import { CameraMode } from '../defines';
import * as THREE from 'three';
import { CameraSettingType } from './CameraSettingsManager';
import { EventDispatcher } from '../EventDispatcher';
import { captureCameraState as sharedCapture, convert2Dto3D as sharedConvert2Dto3D, convert3Dto2D as sharedConvert3Dto2D, type CapturedCameraState } from '../viewer/cameraTransitions';
import { orientationKeyForView, sameOrientation } from './orientation';

/** Events emitted by ViewManager */
export enum ViewManagerEvents {
  /** Available views have changed */
  VIEWS_CHANGED = 'views_changed',
  /** Current view is about to change (fired before any camera modifications) */
  VIEW_WILL_CHANGE = 'view_will_change',
  /** Current view has changed */
  VIEW_CHANGED = 'view_changed',
  /**
   * The switch chain has fully drained after a completed switch: exit/enter
   * hooks, camera conversion and view context are all applied and no further
   * switch is pending. Tools that must write the camera AFTER the view
   * pipeline (e.g. surface alignment) subscribe to this instead of deferring
   * with setTimeout(0) and racing the pipeline's awaits.
   */
  VIEW_SETTLED = 'view_settled',
}

/** Immutable snapshot of view state for React integration via useSyncExternalStore */
export interface ViewManagerSnapshot {
  currentViewId: string | null;
  in2DMode: boolean;
  planeId: string;
  availableViews: ReadonlyArray<{
    id: string;
    name: string;
    description?: string;
    cameraMode?: (typeof CameraMode)[keyof typeof CameraMode];
  }>;
}

/** Captured camera state for seamless view transitions. Re-exported from
 *  the shared module so existing code paths keep their type names. */
type CameraState = CapturedCameraState;

/**
 * Outcome of a switchToView request.
 * - 'switched': the pipeline ran and the view changed.
 * - 'already-active': the requested view was already current (no-op).
 * - 'superseded': a newer switch was requested while this one was queued;
 *   its pipeline was skipped entirely (the last request wins).
 * - 'not-found': the view id is not in the available views.
 */
export type SwitchOutcome = 'switched' | 'already-active' | 'superseded' | 'not-found';

export class ViewManager extends EventDispatcher {
  private viewer: Viewer;

  /** Currently active view (cached for reliable exit) */
  private currentView: IViewMode | null = null;

  /** The view the USER last explicitly clicked (via the ViewSelector), as a
   * sticky preference. On tool/mode switches we try to honour it; if it isn't
   * available in the new tool we clear it and fall back to the tool default.
   * Distinct from `currentView`, which also moves on programmatic switches. */
  private lastUserViewId: string | null = null;

  /** Available views for current context */
  private availableViews: IViewMode[] = [];

  /** Serializes view switches — see switchToView. */
  private switchChain: Promise<void> = Promise.resolve();

  /** Monotonic id of the most recent switch request — used to coalesce
   *  bursts: queued requests that are no longer the latest skip their
   *  pipeline (see switchToView). */
  private latestSwitchRequestId = 0;

  /** Name/context for logging (e.g., "FloorplanPersona", "MonitoringMode") */
  private context: string;

  /** Cached snapshot for React's useSyncExternalStore — replaced on every state change */
  private _snapshot: ViewManagerSnapshot = {
    currentViewId: null,
    in2DMode: false,
    planeId: '',
    availableViews: [],
  };

  constructor(viewer: Viewer, context = 'ViewManager') {
    super();
    this.viewer = viewer;
    this.context = context;
  }

  /**
   * Rebuild the immutable snapshot after any view state change.
   * Creates a new object reference so useSyncExternalStore detects the change.
   */
  private updateSnapshot(): void {
    this._snapshot = {
      currentViewId: this.currentView?.id ?? null,
      in2DMode: this.currentView?.cameraMode === CameraMode.ORTHOGRAPHIC,
      planeId: this.currentView?.viewContext?.planeId ?? '',
      availableViews: this.availableViews.map((v) => ({
        id: v.id,
        name: v.name,
        description: v.description,
        cameraMode: v.cameraMode,
      })),
    };
  }

  /**
   * Get the current snapshot for React's useSyncExternalStore.
   * Returns a stable reference that only changes when view state changes.
   */
  getSnapshot(): ViewManagerSnapshot {
    return this._snapshot;
  }

  /**
   * Subscribe to view state changes for React's useSyncExternalStore.
   * Calls the callback whenever view state changes.
   *
   * @param callback - Function to call when state changes
   * @returns Unsubscribe function
   */
  subscribe(callback: () => void): () => void {
    this.addEventListener(ViewManagerEvents.VIEW_CHANGED, callback);
    this.addEventListener(ViewManagerEvents.VIEWS_CHANGED, callback);
    return () => {
      this.removeEventListener(ViewManagerEvents.VIEW_CHANGED, callback);
      this.removeEventListener(ViewManagerEvents.VIEWS_CHANGED, callback);
    };
  }

  /**
   * Capture the current camera state for seamless view transitions.
   * Thin wrapper over the shared helper, scoped to the global scene's view.
   */
  private captureCameraState(): CameraState {
    return sharedCapture(this.viewer.scene.view, this.viewer.scene.cameraMode);
  }

  /**
   * Convert camera state from 2D to 3D view
   * Places the pivot point at the center of the current 2D view
   * - For floor views: uses floor middle height
   * - For plane views: intersects camera center ray with the plane
   * Similar to WASD→Orbital control switching
   *
   * @param state - The captured camera state
   * @param planeId - Optional plane identifier from the previous view's viewContext
   */
  private async convert2Dto3D(state: CameraState, planeId?: string): Promise<void> {
    // Camera-state math is shared with widget RenderViewState; both write
    // the same shape into a target `View`. Here we hand off the global
    // scene view; widgets pass their orbit controller's view.
    await sharedConvert2Dto3D(this.viewer.scene.view, state, this.viewer, planeId);
  }

  /**
   * Convert camera state from 3D to 2D view
   * Positions camera along the appropriate normal from the pivot point, preserving zoom level
   * Restores saved rotation for the target camera setting type if available
   */
  private async convert3Dto2D(state: CameraState, targetCameraSettingType: CameraSettingType): Promise<void> {
    await sharedConvert3Dto2D(this.viewer.scene.view, state, this.viewer, targetCameraSettingType);
  }

  /**
   * Set the available views for the current context
   * This should be called when persona/mode/tool changes
   * Dispatches VIEWS_CHANGED event when views change
   * If the current view is no longer available, automatically switches to the first available view
   *
   * @param views - Array of available view modes
   */
  async setAvailableViews(views: IViewMode[], options?: { preferredViewId?: string }): Promise<void> {
    this.availableViews = views;
    this.updateSnapshot();
    this.dispatchEvent({
      type: ViewManagerEvents.VIEWS_CHANGED,
      views,
    });

    // Check if current view is still available
    if (this.currentView && views.length > 0) {
      const currentViewStillAvailable = views.some((v) => v.id === this.currentView?.id);
      if (!currentViewStillAvailable) {
        // Current view is no longer available. Fall back to the caller's
        // intended target when provided (e.g. the tool default that
        // enableTool is about to switch to anyway) so the fallback and the
        // explicit switch can't compete; otherwise the first available view.
        const preferred = options?.preferredViewId && views.some((v) => v.id === options.preferredViewId) ? options.preferredViewId : views[0].id;
        await this.switchToView(preferred);
      }
    }
  }

  /**
   * Get the currently active view
   */
  getCurrentView(): IViewMode | null {
    return this.currentView;
  }

  /**
   * Get all available views
   */
  getAvailableViews(): IViewMode[] {
    return this.availableViews;
  }

  /**
   * Switch to a new view
   *
   * Handles complete lifecycle:
   * 1. Capture current camera state for seamless transition
   * 2. Exit current view (if any)
   * 3. Update current view reference
   * 4. Convert camera state based on view transition type
   * 5. Enter new view
   * 6. Apply view mode settings
   * 7. Handle view context (e.g., plane-specific 2D mode)
   *
   * @param viewId - View mode identifier
   * @param viewContext - Optional view context (e.g., planeId for plane-specific orthographic views)
   * @returns the switch outcome (see SwitchOutcome)
   */
  switchToView(viewId: string, viewContext?: IViewMode['viewContext']): Promise<SwitchOutcome> {
    // Serialize switches: a mode change can request several switches in quick
    // succession (availability fallback, tool default, mode default). The
    // switch pipeline is async (camera conversion, view enter/exit hooks) —
    // running them concurrently interleaves those steps and leaves the
    // reported current view disagreeing with what's actually rendered.
    //
    // Coalesce bursts on top of that: a queued request that is no longer the
    // latest skips its pipeline entirely, so a burst runs exactly ONE
    // exit/enter cycle — the last requested view — with no intermediate
    // camera writes or shading flicker.
    const requestId = ++this.latestSwitchRequestId;
    const result = this.switchChain.then((): Promise<SwitchOutcome> | SwitchOutcome => {
      if (requestId !== this.latestSwitchRequestId) {
        return 'superseded';
      }
      return this.performSwitchToView(viewId, viewContext);
    });
    this.switchChain = result.then(
      () => undefined,
      () => undefined,
    );

    // Dispatch VIEW_SETTLED once THIS request completed a real switch and no
    // newer request is pending — the pipeline has fully landed and the chain
    // is idle. (Mirrors VIEW_CHANGED timing: no-op outcomes don't settle.)
    void result
      .then((outcome) => {
        if (outcome === 'switched' && requestId === this.latestSwitchRequestId) {
          this.dispatchEvent({
            type: ViewManagerEvents.VIEW_SETTLED,
            view: this.currentView,
          });
        }
      })
      .catch(() => undefined);

    return result;
  }

  /**
   * Resolves when the switch chain drains — INCLUDING switches enqueued
   * after this call within the same task (e.g. a tool's onEnable runs
   * before BasePersona queues the tool-default switch; awaiting here still
   * lands after that switch). Loops until the chain reference is stable.
   */
  async whenSettled(): Promise<void> {
    let chain: Promise<void>;
    do {
      chain = this.switchChain;
      await chain;
    } while (chain !== this.switchChain);
  }

  private async performSwitchToView(viewId: string, viewContext?: IViewMode['viewContext']): Promise<SwitchOutcome> {
    // Find the new view
    const newView = this.availableViews.find((v) => v.id === viewId);

    if (!newView) {
      console.warn(`[${this.context}] View "${viewId}" not found in available views`);
      return 'not-found';
    }

    // Don't switch if already in this view with same context
    const sameContext = this.currentView?.viewContext?.planeId === viewContext?.planeId;
    if (this.currentView?.id === viewId && sameContext) {
      return 'already-active';
    }

    // Dispatch VIEW_WILL_CHANGE event BEFORE any camera modifications
    // This allows listeners to save camera state before it's modified
    this.dispatchEvent({
      type: ViewManagerEvents.VIEW_WILL_CHANGE,
      previousView: this.currentView,
      nextView: newView,
    });

    // Cancel in-flight camera tweens (moveTo/zoomTo/setView/orbit zoom) —
    // a surviving tween would keep writing the pose for up to its full
    // duration AFTER this switch lands, overwriting the new view's camera.
    this.viewer.cancelCameraMotion();

    // Capture camera state BEFORE exiting current view for seamless transitions
    const previousCameraState = this.captureCameraState();
    const previousView = this.currentView;

    // Exit current view (await to ensure onExit cleanup completes)
    await this.exitCurrentView();

    // Update current view reference
    const viewWithContext = viewContext ? { ...newView, viewContext } : newView;

    this.currentView = viewWithContext;

    // Determine if this is a 2D -> 2D transition (should preserve camera position/rotation)
    const was2D = previousView?.cameraMode === CameraMode.ORTHOGRAPHIC;
    const is2D = viewWithContext.cameraMode === CameraMode.ORTHOGRAPHIC;
    const is2Dto2DTransition = was2D && is2D;

    // Apply camera state conversion for seamless transitions
    await this.convertCameraState(previousView, viewWithContext, previousCameraState);

    // Enter new view (await to ensure onEnter setup completes)
    await this.enterView(viewWithContext);

    // Apply view mode
    this.applyViewMode(viewWithContext);

    // Apply view context (skip camera adjustment for 2D -> 2D transitions)
    await this.applyViewContext(viewWithContext, is2Dto2DTransition, previousView);

    // Dispatch view changed event
    this.updateSnapshot();
    this.dispatchEvent({
      type: ViewManagerEvents.VIEW_CHANGED,
      view: viewWithContext,
    });

    return 'switched';
  }

  /**
   * Convert camera state based on the type of view transition
   * Enables seamless transitions between 2D and 3D views
   */
  private async convertCameraState(previousView: IViewMode | null, newView: IViewMode, cameraState: CameraState): Promise<void> {
    // Same orientation class ⇒ the pose (including the user's rotation)
    // carries over verbatim — zero camera writes ("light switch"). This is
    // the invariant that keeps tool switches within the same visual view
    // (X-Ray / 2D-top / Plan) from ever realigning the screen.
    if (sameOrientation(previousView, newView)) {
      return;
    }

    const was2D = previousView?.cameraMode === CameraMode.ORTHOGRAPHIC;
    const is2D = newView.cameraMode === CameraMode.ORTHOGRAPHIC;

    if (was2D && !is2D) {
      // 2D -> 3D: Convert orthographic view to perspective
      // Pass the previous view's planeId to look up the plane directly
      // (viewerMode.associatedPlane may already be cleared at this point)
      await this.convert2Dto3D(cameraState, previousView?.viewContext?.planeId);
    } else if (!was2D && is2D) {
      // 3D -> 2D: Convert perspective view to orthographic
      // Determine the target camera setting type for the 2D view
      const targetCameraSettingType = this.viewer.cameraSettingsManager?.getCameraSettingTypeFromViewMode(newView.cameraMode, newView.viewContext?.planeId) ?? CameraSettingType.ORTHOGRAPHIC_TOP;
      await this.convert3Dto2D(cameraState, targetCameraSettingType);
    }
    // 2D -> 2D across orientation classes needs no conversion here:
    // applyViewContext routes it through enable2DMode(...), which performs
    // the full class-entry realignment (pitch + saved roll for top-down,
    // plane basis for plane views).
  }

  /**
   * Exit the current view (if any)
   * Safe to call multiple times
   */
  async exitCurrentView(): Promise<void> {
    if (!this.currentView) {
      return;
    }

    const view = this.currentView;

    if (view.onExit) {
      try {
        const result = view.onExit(this.viewer);
        // Await async onExit to ensure cleanup completes before entering new view
        if (result instanceof Promise) {
          await result;
        }
      } catch (error) {
        console.error(`[${this.context}] Error in view "${view.id}" onExit:`, error);
      }
    }

    // Clear current view reference
    this.currentView = null;
  }

  /**
   * Enter a new view
   *
   * @param view - View to enter
   */
  private async enterView(view: IViewMode): Promise<void> {
    if (view.onEnter) {
      try {
        const result = view.onEnter(this.viewer);
        // Await async onEnter to ensure setup completes
        if (result instanceof Promise) {
          await result;
        }
      } catch (error) {
        console.error(`[${this.context}] Error in view "${view.id}" onEnter:`, error);
      }
    }
  }

  /**
   * Apply view mode settings (2D/3D camera configuration)
   *
   * @param view - View mode to apply
   */
  private applyViewMode(view: IViewMode): void {
    if (!view.mode) return;

    switch (view.mode) {
      case '2D':
        // Switch to orthographic camera if not already. The camera POSE is
        // owned by the View/orbit system (derived per frame in
        // viewer.update()) — the direct THREE-camera writes that used to sit
        // here (position/lookAt/up=(0,1,0)) fought that derivation with a
        // Y-up assumption in a Z-up codebase and were overwritten on the
        // next frame anyway.
        if (this.viewer.scene.cameraMode !== CameraMode.ORTHOGRAPHIC) {
          this.viewer.setCameraMode(CameraMode.ORTHOGRAPHIC);
        }
        break;

      case '3D':
        // Enable full 3D navigation
        if (this.viewer.scene.cameraMode !== CameraMode.PERSPECTIVE) {
          this.viewer.setCameraMode(CameraMode.PERSPECTIVE);
        }
        break;

      default:
        console.warn(`[${this.context}] Unknown view mode: ${view.mode}`);
    }

    // Apply map visibility
    if (view.mapVisible !== undefined) {
      if (view.mapVisible) {
        this.viewer.showMap?.();
      } else {
        this.viewer.hideMap?.();
      }
    }
  }

  /**
   * Apply view context (e.g., plane-specific 2D mode)
   *
   * This is the single place that toggles EDL for 2D/3D transitions:
   * - Entering a 2D (orthographic) view suppresses EDL without touching the
   *   user's SHOW_EDGES preference.
   * - Entering a 3D (perspective) view restores EDL from that preference.
   *
   * Individual view modes and ViewerMode's enter/exit 2D methods no longer
   * manage EDL themselves — they just set up camera geometry.
   *
   * @param view - View mode with context
   * @param preserveCamera - If true, only update 2D mode state/shaders without adjusting camera (for 2D -> 2D transitions)
   * @param previousView - The view being left (null on the first switch)
   */
  private async applyViewContext(view: IViewMode, preserveCamera = false, previousView: IViewMode | null = null): Promise<void> {
    if (!this.viewer.viewerMode) {
      console.warn(`[${this.context}] ViewerMode not initialized`);
      return;
    }

    const { planeId } = view.viewContext || {};

    if (view.cameraMode === CameraMode.ORTHOGRAPHIC) {
      // Camera identity is keyed on the orientation class (see
      // personas/orientation.ts), not on view-id strings or the (possibly
      // stale) viewerMode.associatedPlane. Same key ⇒ the pose carries over
      // verbatim; different key ⇒ full class-entry realignment.
      const targetKey = orientationKeyForView(view);
      const preserve = preserveCamera && sameOrientation(previousView, view);

      // Plane-class views without a plane context (xray_2d_surface /
      // xray_2d_wall shells) have TOOL-managed cameras: the activating tool
      // aligns the view itself. The ViewManager must not realign them.
      const toolManagedCamera = targetKey === 'plane_2d:' && !planeId;

      if (preserve || toolManagedCamera) {
        // Update state and shaders only, leave the camera untouched.
        this.viewer.viewerMode.in2DMode = true;
        this.viewer.setCameraMode(CameraMode.ORTHOGRAPHIC);
      } else if (planeId) {
        // Switching to a specific plane - need full camera setup
        this.viewer.viewerMode.enable2DMode(planeId);
      } else {
        // Entering the top-down class - full camera setup (pitch + saved
        // floor rotation, applied synchronously in enable2DModeFromMap)
        this.viewer.viewerMode.enable2DMode('map');
      }

      // Single chokepoint for EDL suppression in 2D views
      this.viewer.viewerMode.suppressEDLForMode();
    } else if (view.cameraMode === CameraMode.PERSPECTIVE) {
      this.viewer.viewerMode.disable2DMode();
      // Single chokepoint for EDL restoration on return to 3D
      this.viewer.viewerMode.restoreEDLFromPreference();
    }
  }

  /**
   * Clean up and exit current view
   * Call this when the owning persona/mode is being disabled
   */
  cleanup(): void {
    this.exitCurrentView();
    this.availableViews = [];
    this.updateSnapshot();
  }

  /**
   * Update the context name for logging
   *
   * @param context - New context name
   */
  setContext(context: string): void {
    this.context = context;
  }

  /**
   * Switch to a view by ID.
   * Convenience wrapper around switchToView for React consumers.
   */
  setViewById(viewId: string): void {
    // A user-initiated view switch — remember it as the sticky preference.
    this.lastUserViewId = viewId;
    this.switchToView(viewId);
  }

  /** The user's last explicitly-clicked view id (sticky preference), or null. */
  getLastUserViewId(): string | null {
    return this.lastUserViewId;
  }

  /** Forget the sticky user view preference (e.g. when it isn't available in
   * the tool/mode just switched to). */
  clearLastUserViewId(): void {
    this.lastUserViewId = null;
  }

  /**
   * Enable 2D (orthographic) mode on a given plane.
   * Finds the orthographic view from available views and switches to it.
   *
   * @param planeId - Plane identifier (e.g., 'map', annotation identifier)
   */
  enable2DViewMode(planeId: string): void {
    const orthographicView = this.availableViews.find((v) => v.cameraMode === CameraMode.ORTHOGRAPHIC);
    if (orthographicView) {
      this.switchToView(orthographicView.id, { planeId });
    }
  }

  /**
   * Toggle 2D mode for a given plane.
   * - If already in 2D on same plane: switch to 3D + reset tool
   * - If in 2D on different plane: switch to that plane
   * - If in 3D: switch to 2D on the given plane
   *
   * @param planeId - Plane identifier to toggle
   */
  toggle2DViewMode(planeId: string): void {
    const orthographicView = this.availableViews.find((v) => v.cameraMode === CameraMode.ORTHOGRAPHIC);
    const perspectiveView = this.availableViews.find((v) => v.cameraMode === CameraMode.PERSPECTIVE);

    const isIn2D = this.currentView?.cameraMode === CameraMode.ORTHOGRAPHIC;
    const currentPlaneId = this.currentView?.viewContext?.planeId ?? '';

    if (isIn2D) {
      if (currentPlaneId === planeId) {
        // Same plane — switch to 3D
        if (perspectiveView) {
          this.switchToView(perspectiveView.id);
        }
        this.viewer.personaManager?.getActivePersona()?.resetToDefaultTool();
      } else {
        // Different plane — switch to that plane
        if (orthographicView) {
          this.switchToView(orthographicView.id, { planeId });
        }
      }
    } else {
      // In 3D — switch to 2D
      if (orthographicView) {
        this.switchToView(orthographicView.id, { planeId });
      }
    }
  }

  /**
   * Disable 2D mode and return to 3D perspective view.
   * Resets the active tool to the default.
   */
  disable2DViewMode(): void {
    if (this.currentView?.cameraMode !== CameraMode.ORTHOGRAPHIC) return;

    const perspectiveView = this.availableViews.find((v) => v.cameraMode === CameraMode.PERSPECTIVE);

    this.viewer.personaManager?.getActivePersona()?.resetToDefaultTool();

    if (perspectiveView) {
      this.switchToView(perspectiveView.id);
    }
  }

  /**
   * Check if the current view can be flipped
   * Returns true for plane-specific or line-specific 2D views
   */
  canFlip(): boolean {
    if (!this.currentView || !this.viewer.viewerMode) {
      return false;
    }

    if (this.currentView.cameraMode !== CameraMode.ORTHOGRAPHIC) {
      return false;
    }

    const planeId = this.currentView.viewContext?.planeId;
    const hasContextObject = !!planeId && !(planeId in ['map', 'side']);

    return hasContextObject && this.viewer.viewerMode.canFlip;
  }

  /**
   * Flip the current 2D view (plane or line)
   * Inverts the plane normal or swaps line points, then refreshes the view
   */
  flipView(): void {
    if (!this.canFlip() || !this.viewer.viewerMode) {
      return;
    }

    const planeId = this.currentView?.viewContext?.planeId;
    if (!planeId) return;

    if (this.viewer.viewerMode.hasVirtualPlane && this.viewer.viewerMode.associatedLine) {
      const line = this.viewer.viewerMode.associatedLine;
      const points = line.points;
      if (points.length !== 2) return;

      const p1 = points[0].position.clone();
      const p2 = points[1].position.clone();
      points[0].position.copy(p2);
      points[1].position.copy(p1);
    } else if (this.viewer.viewerMode.associatedPlane) {
      const plane = this.viewer.viewerMode.associatedPlane;
      const invertedNormal = new THREE.Vector3(-plane.normal.x, -plane.normal.y, -plane.normal.z);
      plane.setRotationFromNormal(invertedNormal);
      plane.calculateInclinationAndBearing();
    }

    this.viewer.viewerMode.enable2DMode(planeId);
  }
}
